<template>
  <div>
    <el-row :gutter="20" class="top-block">
      <Breadcrumb :route="this.$route"></Breadcrumb>
    </el-row>

    <el-row class="ml-20 mr-20">
      <el-col :xs="12" :sm="12" :lg="12">
        <el-button icon="el-icon-search" circle type="warning" @click="drawer = true"></el-button>
      </el-col>
      <el-col :xs="12" :sm="12" :lg="12" style="text-align: right">
        <el-button
          type="primary"
          :disabled="multipleSelection.length === 0 || exporting"
          :loading="exporting"
          @click="handleExport"
          class="mr-20">
          <i class="el-icon-download" v-if="!exporting"></i>
          {{ exporting ? exportProgressText : `匯出 (${multipleSelection.length})` }}
        </el-button>
      </el-col>
    </el-row>

    <el-row>
      <el-col :xs="24" :sm="24" :lg="24">
        <el-main>
          <div class="ml-20" v-if="didQuery">
            <p>查詢區間: {{ startDate }} ~ {{ endDate }}
              <span v-if="empLabel"> | 人員: {{ empLabel }}</span>
            </p>
          </div>
          <el-table
            class="mt-20"
            :data="tableData"
            empty-text="目前尚無資料"
            stripe
            header-row-class-name="headerStyle"
            @selection-change="handleSelectChange">
            <el-table-column type="selection" width="45" :selectable="canSelect"></el-table-column>
            <el-table-column prop="mytodo.startDate" label="日期" width="110">
              <template slot-scope="scope">
                {{ scope.row.mytodo.startDate | fmtDate }}
              </template>
            </el-table-column>
            <el-table-column label="申請人" width="100">
              <template slot-scope="scope">
                {{ scope.row.relatedUserObj.username }}
              </template>
            </el-table-column>
            <el-table-column label="類型" width="100">
              <template slot-scope="scope">
                {{ scope.row.mytodo.todoType | fmtTodoType }}
              </template>
            </el-table-column>
            <el-table-column label="標題">
              <template slot-scope="scope">
                {{ scope.row.mytodo.title }}
              </template>
            </el-table-column>
            <el-table-column label="簽核狀態" min-width="110">
              <template slot-scope="scope">
                <el-tag :type="statusTagType(scope.row.mytodo.status)" size="small">
                  {{ scope.row.mytodo.status | fmtSignResult }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>

          <div v-if="total > 0" style="border-radius:8px; margin-top:10px; padding:20px 10px; text-align:center; background-color:white">
            <el-pagination
              background
              layout="prev, pager, next"
              :current-page="pageNum"
              :page-size="pageSize"
              :total="total"
              @current-change="handleCurrentChange">
            </el-pagination>
          </div>
        </el-main>
      </el-col>
    </el-row>

    <!-- 查詢 Drawer -->
    <el-drawer
      size="70%"
      title="日報匯出查詢"
      :visible.sync="drawer"
      direction="ltr"
      :before-close="handleDrawerClose">
      <el-main>
        <el-row class="mr-20">
          <el-col :xs="24" :sm="24" :lg="24">
            <span class="demonstration">開始日期</span>
            <el-date-picker
              v-model="startDate"
              type="date"
              format="yyyy/MM/dd"
              value-format="yyyy/MM/dd"
              placeholder="開始日期"
              class="mt-10"
              style="width:100%">
            </el-date-picker>
          </el-col>
        </el-row>
        <el-row class="mr-20 mt-20">
          <el-col :xs="24" :sm="24" :lg="24">
            <span class="demonstration">結束日期</span>
            <el-date-picker
              v-model="endDate"
              type="date"
              format="yyyy/MM/dd"
              value-format="yyyy/MM/dd"
              placeholder="結束日期"
              class="mt-10"
              style="width:100%">
            </el-date-picker>
          </el-col>
        </el-row>
        <el-row class="mr-20 mt-20">
          <el-col :xs="24" :sm="24" :lg="24">
            <span class="demonstration">人員</span>
            <el-autocomplete
              v-model="empLabel"
              :fetch-suggestions="queryEmp"
              placeholder="輸入人員姓名"
              @select="handleEmpSelect"
              class="mt-10"
              style="width:100%"
              clearable
              @clear="empId = ''">
            </el-autocomplete>
          </el-col>
        </el-row>
      </el-main>
      <el-button type="primary" @click.prevent="search" class="ml-20">查詢</el-button>
      <el-button type="normal" @click.prevent="clearQuery" class="ml-10">清除條件</el-button>
    </el-drawer>

    <!-- 匯出進度 Dialog -->
    <el-dialog
      title="匯出進度"
      :visible.sync="exportDialog"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      width="400px">
      <div style="text-align:center">
        <el-progress :percentage="exportPercent" :status="exportStatus"></el-progress>
        <p class="mt-20">{{ exportProgressText }}</p>
        <div v-if="exportErrors.length > 0" class="mt-20" style="text-align:left">
          <p style="color:#F56C6C">匯出失敗項目：</p>
          <ul>
            <li v-for="(err, idx) in exportErrors" :key="idx" style="color:#F56C6C; font-size:12px">
              {{ err }}
            </li>
          </ul>
        </div>
      </div>
      <div slot="footer" v-if="!exporting">
        <el-button type="primary" @click="exportDialog = false">關閉</el-button>
      </div>
    </el-dialog>

    <!-- 隱藏的 PDF 渲染容器 -->
    <div ref="printContainer" class="print-container">
      <!-- 基本資料區 -->
      <div v-if="currentExportData" class="print-page">
        <h2 style="text-align:center; margin-bottom:20px;">{{ currentExportData.todoInfo.title }}</h2>
        <table class="print-table">
          <tr>
            <td class="print-label">人員</td>
            <td>{{ currentExportData.todoInfo.userObj ? currentExportData.todoInfo.userObj.username : '' }}</td>
            <td class="print-label">報表日期</td>
            <td>{{ formatDate(currentExportData.todoInfo.startDate) }}</td>
          </tr>
          <tr v-if="currentExportData.todoInfo.todoType === 'for_district_manager' || currentExportData.todoInfo.todoType === 'for_fin_manager'">
            <td class="print-label">內部管理報告</td>
            <td colspan="3" v-html="currentExportData.todoInfo.notes || ''"></td>
          </tr>
        </table>

        <!-- Driver 類: 出勤前工作、路線、拜訪間數 -->
        <div v-if="isDriverType(currentExportData.todoInfo.todoType)">
          <table class="print-table mt-10" v-if="currentExportData.todoInfo.preCheckItem">
            <tr>
              <td class="print-label">出勤前工作</td>
              <td>{{ currentExportData.todoInfo.preCheckItem }}</td>
            </tr>
            <tr v-if="currentExportData.todoInfo.visitRoute">
              <td class="print-label">路線</td>
              <td>{{ currentExportData.todoInfo.visitRoute }}</td>
            </tr>
            <tr v-if="currentExportData.todoInfo.visitCount">
              <td class="print-label">拜訪間數</td>
              <td>{{ currentExportData.todoInfo.visitCount }}</td>
            </tr>
          </table>
        </div>

        <!-- 工作項目 (divMgrHistory) -->
        <div v-if="currentExportData.divMgrHistory && currentExportData.divMgrHistory.length > 0" class="mt-10">
          <h3>工作項目</h3>
          <table class="print-table">
            <thead>
              <tr>
                <th width="40">項次</th>
                <th>項目</th>
                <th>內容</th>
                <th width="100">時長</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in currentExportData.divMgrHistory" :key="'div'+idx">
                <td>{{ idx + 1 }}</td>
                <td>{{ item.taskTypeName || item.taskType }}</td>
                <td v-html="fmtNotes(item.notes || '')"></td>
                <td>{{ item.timeHour }}時{{ item.timeMinute }}分</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 彩繪工作項目 (paintingHistory) -->
        <div v-if="currentExportData.paintingHistory && currentExportData.paintingHistory.length > 0" class="mt-10">
          <h3>工作項目</h3>
          <table class="print-table">
            <thead>
              <tr>
                <th width="40">項次</th>
                <th>產品</th>
                <th>效果</th>
                <th>數量</th>
                <th>進度</th>
                <th width="100">時長</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in currentExportData.paintingHistory" :key="'paint'+idx">
                <td>{{ idx + 1 }}</td>
                <td>{{ item.product }}</td>
                <td>{{ item.result }} {{ item.resultNotes }}</td>
                <td>{{ item.qty }}</td>
                <td>{{ item.progress }}</td>
                <td>{{ item.timeHour }}時{{ item.timeMinute }}分</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 客戶拜訪紀錄 (customerHistory) -->
        <div v-if="currentExportData.customerHistory && currentExportData.customerHistory.length > 0" class="mt-10">
          <h3>客戶拜訪紀錄</h3>
          <table class="print-table">
            <thead>
              <tr>
                <th width="40">項次</th>
                <th>客戶</th>
                <th>服務內容</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in currentExportData.customerHistory" :key="'cust'+idx">
                <td>{{ idx + 1 }}</td>
                <td>{{ item.customer ? item.customer.company : (item.company || '') }}</td>
                <td>{{ fmtServiceContent(item) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 新客戶拜訪紀錄 (newCustomerHistory) -->
        <div v-if="currentExportData.newCustomerHistory && currentExportData.newCustomerHistory.length > 0" class="mt-10">
          <h3>新客戶拜訪紀錄</h3>
          <table class="print-table">
            <thead>
              <tr>
                <th width="40">項次</th>
                <th>客戶</th>
                <th>服務內容</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in currentExportData.newCustomerHistory" :key="'newcust'+idx">
                <td>{{ idx + 1 }}</td>
                <td>{{ item.customer ? item.customer.company : (item.company || '') }}</td>
                <td>{{ fmtServiceContent(item) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 其他工作事項 (taskContent) -->
        <div v-if="currentExportData.todoInfo.taskContent" class="mt-10">
          <h3>其他工作事項</h3>
          <p style="padding:8px" v-html="fmtNotes(currentExportData.todoInfo.taskContent)"></p>
        </div>

        <!-- 哩程紀錄 (drivingHistory) -->
        <div v-if="currentExportData.drivingHistory && currentExportData.drivingHistory.length > 0" class="mt-10">
          <h3>交通工具與里程數</h3>
          <table class="print-table">
            <thead>
              <tr>
                <th>交通工具</th>
                <th>開始哩程</th>
                <th>結束哩程</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in currentExportData.drivingHistory" :key="'drive'+idx">
                <td>{{ item.carType }}</td>
                <td>{{ item.startMile }}</td>
                <td>{{ item.endMile }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 簽核紀錄 -->
        <div v-if="currentExportData.signoffHistory && currentExportData.signoffHistory.length > 0" class="mt-10">
          <h3>簽核紀錄</h3>
          <table class="print-table">
            <thead>
              <tr>
                <th width="150">簽核時間</th>
                <th>簽核人員</th>
                <th width="60">結果</th>
                <th>備註</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in currentExportData.signoffHistory" :key="'sign'+idx">
                <td>{{ formatDateTime(item.updatedAt) }}</td>
                <td>{{ item.signerObj ? item.signerObj.username : '' }}</td>
                <td>{{ item.signResult === 'Y' ? '同意' : (item.signResult === 'N' ? '退回' : item.signResult) }}</td>
                <td>{{ item.notes }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Breadcrumb from '@/components/breadcrumb/Breadcrumb'
import { requestDailyExportItems, requestJobDetail } from '@/api/todo-api'
import dayjs from 'dayjs'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

// Phase 1 支援的 todoType
const SUPPORTED_TYPES = [
  'for_painting',
  'for_driver',
  'for_product_sales',
  'for_sales_driver',
  'for_brand_sales',
  'for_district_manager',
  'for_fin_manager',
  'for_oa',
  'for_oa_acct',
  'for_oa_adm',
  'for_oa_planning'
]

const DRIVER_TYPES = ['for_driver', 'for_product_sales', 'for_sales_driver', 'for_brand_sales', 'for_district_manager']

const MAX_EXPORT = 20

export default {
  name: 'ExportDailyTodo',
  components: {
    Breadcrumb
  },
  data() {
    return {
      drawer: false,
      startDate: '',
      endDate: '',
      empId: '',
      empLabel: '',
      tableData: [],
      multipleSelection: [],
      pageNum: 1,
      pageSize: 50,
      total: 0,
      didQuery: false,

      // 匯出狀態
      exporting: false,
      exportDialog: false,
      exportCurrent: 0,
      exportTotal: 0,
      exportErrors: [],
      exportStatus: '',

      // 隱藏渲染用
      currentExportData: null
    }
  },
  computed: {
    exportPercent() {
      if (this.exportTotal === 0) return 0
      return Math.round((this.exportCurrent / this.exportTotal) * 100)
    },
    exportProgressText() {
      if (!this.exporting && this.exportCurrent === this.exportTotal && this.exportTotal > 0) {
        return `匯出完成 (${this.exportTotal} 筆)`
      }
      return `匯出中 ${this.exportCurrent} / ${this.exportTotal}`
    }
  },
  methods: {
    fmtServiceContent(item) {
      const parts = []
      if (item.deliveryContent) parts.push('送貨: ' + item.deliveryContent)
      if (item.paymentReceive) parts.push('收款: ' + item.paymentReceive)
      if (item.marketing) parts.push('宣傳: ' + item.marketing)
      if (item.otherService) parts.push('其它服務: ' + item.otherService)
      if (item.notes) parts.push('客戶回饋: ' + item.notes)
      return parts.join('；')
    },
    handleDrawerClose() {
      this.drawer = false
    },
    queryEmp(str, cb) {
      this.request.get('/user/q?keyword=' + str).then(res => {
        if (res.code === '200') {
          let list = res.data.map(item => ({
            value: item.username,
            id: item.id
          }))
          cb(list)
        } else {
          cb([])
        }
      })
    },
    handleEmpSelect(item) {
      this.empId = item.id
      this.empLabel = item.value
    },
    search() {
      if (!this.startDate || !this.endDate) {
        this.showResult('warning', '請選擇開始與結束日期')
        return
      }
      this.drawer = false
      this.pageNum = 1
      this.didQuery = true
      this.loadData()
    },
    clearQuery() {
      this.startDate = dayjs().format('YYYY/MM/DD')
      this.endDate = dayjs().format('YYYY/MM/DD')
      this.empId = ''
      this.empLabel = ''
    },
    loadData() {
      requestDailyExportItems({
        startDate: this.startDate,
        endDate: this.endDate,
        empId: this.empId,
        pageNum: this.pageNum,
        pageSize: this.pageSize
      }).then(res => {
        if (res.code === '200') {
          this.tableData = res.data.records || []
          this.total = res.data.total || 0
        } else {
          this.showResult('error', '資料取得失敗')
        }
      })
    },
    handleCurrentChange(val) {
      this.pageNum = val
      this.loadData()
    },
    handleSelectChange(val) {
      this.multipleSelection = val
    },
    canSelect(row) {
      return this.isExportSupported(row.mytodo ? row.mytodo.todoType : row.todoType)
    },
    isExportSupported(todoType) {
      return SUPPORTED_TYPES.includes(todoType)
    },
    isDriverType(todoType) {
      return DRIVER_TYPES.includes(todoType)
    },
    statusTagType(status) {
      if (status === 'close') return 'success'
      if (status === 'review') return 'warning'
      if (status === 'reject') return 'danger'
      return 'info'
    },
    formatDate(val) {
      if (!val) return ''
      return dayjs(val).format('YYYY-MM-DD')
    },
    formatDateTime(val) {
      if (!val) return ''
      return dayjs(val).format('YYYY-MM-DD HH:mm')
    },

    async handleExport() {
      const selected = this.multipleSelection.filter(row =>
        this.isExportSupported(row.mytodo ? row.mytodo.todoType : row.todoType)
      )

      if (selected.length === 0) {
        this.showResult('warning', '請勾選可匯出的日報')
        return
      }

      if (selected.length > MAX_EXPORT) {
        this.showResult('warning', `單次匯出上限 ${MAX_EXPORT} 筆，目前勾選 ${selected.length} 筆`)
        return
      }

      this.exporting = true
      this.exportDialog = true
      this.exportCurrent = 0
      this.exportTotal = selected.length
      this.exportErrors = []
      this.exportStatus = ''

      const zip = new JSZip()
      const fileNameCount = {}

      for (let i = 0; i < selected.length; i++) {
        const row = selected[i]
        this.exportCurrent = i + 1
        const jobId = row.id
        const username = row.relatedUserObj ? row.relatedUserObj.username : 'unknown'
        const dateStr = row.mytodo ? this.formatDate(row.mytodo.startDate) : ''

        try {
          // 1. 取得完整 detail
          const res = await requestJobDetail(jobId)
          if (res.code !== '200') {
            this.exportErrors.push(`${username}_${dateStr}: 資料取得失敗`)
            continue
          }

          // 2. 渲染到隱藏容器
          this.currentExportData = res.data
          await this.$nextTick()

          // 等一小段時間確保渲染完成
          await new Promise(resolve => setTimeout(resolve, 200))

          // 3. html2canvas 截圖 - 暫時讓容器可見以取得正確寬高
          const container = this.$refs.printContainer
          container.style.visibility = 'visible'
          container.style.height = 'auto'
          container.style.overflow = 'visible'
          container.style.position = 'fixed'
          container.style.left = '0'
          container.style.top = '0'
          container.style.zIndex = '-1'

          await new Promise(resolve => setTimeout(resolve, 100))

          const canvas = await html2canvas(container, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            width: container.scrollWidth,
            height: container.scrollHeight,
            windowWidth: container.scrollWidth
          })

          // 恢復隱藏
          container.style.visibility = 'hidden'
          container.style.height = '0'
          container.style.overflow = 'hidden'
          container.style.position = 'absolute'
          container.style.left = '0'
          container.style.zIndex = '-1'

          // 4. 生成 PDF
          const imgData = canvas.toDataURL('image/jpeg', 0.95)
          const pdfWidth = 210 // A4 mm
          const margin = 10
          const contentWidth = pdfWidth - margin * 2
          const contentHeight = (canvas.height * contentWidth) / canvas.width
          const pageHeight = 297 // A4
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
          })

          // 若內容超過一頁則分頁
          if (contentHeight <= pageHeight - margin * 2) {
            pdf.addImage(imgData, 'JPEG', margin, margin, contentWidth, contentHeight)
          } else {
            let remainingHeight = canvas.height
            let position = 0
            const pageCanvas = document.createElement('canvas')
            const pageCtx = pageCanvas.getContext('2d')
            const ratio = canvas.width / contentWidth
            const pagePixelHeight = (pageHeight - margin * 2) * ratio

            let pageIndex = 0
            while (remainingHeight > 0) {
              if (pageIndex > 0) pdf.addPage()
              const sliceHeight = Math.min(pagePixelHeight, remainingHeight)
              pageCanvas.width = canvas.width
              pageCanvas.height = sliceHeight
              pageCtx.fillStyle = '#ffffff'
              pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)
              pageCtx.drawImage(canvas, 0, position, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight)
              const pageImg = pageCanvas.toDataURL('image/jpeg', 0.95)
              const sliceMmHeight = (sliceHeight * contentWidth) / canvas.width
              pdf.addImage(pageImg, 'JPEG', margin, margin, contentWidth, sliceMmHeight)
              position += sliceHeight
              remainingHeight -= sliceHeight
              pageIndex++
            }
          }

          // 5. 產生檔名 (人員_日期.pdf)
          let baseName = `${username}_${dateStr}`
          if (fileNameCount[baseName]) {
            fileNameCount[baseName]++
            baseName = `${baseName}_${fileNameCount[baseName]}`
          } else {
            fileNameCount[baseName] = 1
          }

          zip.file(`${baseName}.pdf`, pdf.output('arraybuffer'))
        } catch (err) {
          console.error('Export error:', err)
          this.exportErrors.push(`${username}_${dateStr}: ${err.message || '匯出失敗'}`)
        }
      }

      // 清除渲染資料
      this.currentExportData = null

      // 6. 下載 ZIP
      try {
        const content = await zip.generateAsync({ type: 'blob' })
        const zipName = `日報匯出_${dayjs().format('YYYYMMDD_HHmm')}.zip`
        saveAs(content, zipName)
        this.exportStatus = 'success'
      } catch (err) {
        this.exportErrors.push('ZIP 打包失敗: ' + err.message)
        this.exportStatus = 'exception'
      }

      this.exporting = false

      if (this.exportErrors.length === 0) {
        this.showResult('success', `匯出完成，共 ${this.exportTotal} 筆`)
      } else {
        this.showResult('warning', `匯出完成，${this.exportErrors.length} 筆失敗`)
      }
    }
  },
  mounted() {
    this.startDate = dayjs().format('YYYY/MM/DD')
    this.endDate = dayjs().format('YYYY/MM/DD')
  }
}
</script>

<style scoped>
.el-table {
  border-radius: 10px;
  padding: 20px;
}

.print-container {
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
  visibility: hidden;
  height: 0;
  overflow: hidden;
  width: 800px;
  background: #fff;
  padding: 30px;
  font-family: 'Microsoft JhengHei', 'PingFang TC', sans-serif;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
}

.print-page {
  width: 100%;
}

.print-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
}

.print-table th,
.print-table td {
  border: 1px solid #999;
  padding: 6px 10px;
  text-align: left;
  vertical-align: top;
  font-size: 13px;
}

.print-table th {
  background-color: #f0f0f0;
  font-weight: bold;
}

.print-label {
  background-color: #f5f5f5;
  font-weight: bold;
  width: 100px;
}

.mt-10 {
  margin-top: 10px;
}
</style>
